import { useMemo } from 'react';
import { MenuItem } from '../../../services/dataService';

function matchesSearch(item: MenuItem, searchLower: string): boolean {
  return (
    item.name.toLowerCase().includes(searchLower) ||
    Boolean(item.description?.toLowerCase().includes(searchLower)) ||
    item.category.toLowerCase().includes(searchLower) ||
    Boolean(item.badges?.some((badge) => badge.toLowerCase().includes(searchLower))) ||
    Boolean(item.dietaryInfo?.some((info) => info.toLowerCase().includes(searchLower))) ||
    Boolean(item.tags?.some((tag) => tag.toLowerCase().includes(searchLower))) ||
    (Boolean(item.isSpecialOffer) && 'special offer'.includes(searchLower)) ||
    (Boolean(item.isFeatured) && ('featured'.includes(searchLower) || 'highlight'.includes(searchLower))) ||
    (Boolean(item.isTodaysSpecial) &&
      ('today special'.includes(searchLower) ||
        'todays special'.includes(searchLower) ||
        "today's special".includes(searchLower) ||
        'daily special'.includes(searchLower))) ||
    (item.preparationTime <= 5 && ('quick'.includes(searchLower) || 'fast'.includes(searchLower)))
  );
}

export function usePublicMenuFilters(
  menuItems: MenuItem[],
  searchTerm: string,
  selectedCategory: string
) {
  const categories = useMemo(() => {
    const regularCategories = Array.from(new Set(menuItems.map((item) => item.category))).sort();
    const specialCategories: string[] = [];
    if (menuItems.some((item) => item.isTodaysSpecial)) {
      specialCategories.push("Today's Specials");
    }
    if (menuItems.some((item) => item.isFeatured && !item.isTodaysSpecial)) {
      specialCategories.push('Featured Items');
    }
    return ['All', ...specialCategories, ...regularCategories];
  }, [menuItems]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    counts["Today's Specials"] = menuItems.filter((i) => i.isTodaysSpecial).length;
    counts['Featured Items'] = menuItems.filter((i) => i.isFeatured && !i.isTodaysSpecial).length;
    for (const item of menuItems) {
      counts[item.category] = (counts[item.category] ?? 0) + 1;
    }
    return counts;
  }, [menuItems]);

  const filteredItems = useMemo(() => {
    let filtered = menuItems;

    if (selectedCategory !== 'All') {
      if (selectedCategory === "Today's Specials") {
        filtered = filtered.filter((item) => item.isTodaysSpecial);
      } else if (selectedCategory === 'Featured Items') {
        filtered = filtered.filter((item) => item.isFeatured && !item.isTodaysSpecial);
      } else {
        filtered = filtered.filter((item) => item.category === selectedCategory);
      }
    }

    const trimmed = searchTerm.trim();
    if (trimmed) {
      const searchLower = trimmed.toLowerCase();
      filtered = filtered.filter((item) => matchesSearch(item, searchLower));
    }

    return filtered;
  }, [menuItems, searchTerm, selectedCategory]);

  const groupedItems = useMemo((): Record<string, MenuItem[]> => {
    if (selectedCategory === "Today's Specials") {
      const itemsToGroup = filteredItems.filter((item) => item.isTodaysSpecial);
      return { "Today's Specials": itemsToGroup } as Record<string, MenuItem[]>;
    }
    if (selectedCategory === 'Featured Items') {
      const itemsToGroup = filteredItems.filter((item) => item.isFeatured && !item.isTodaysSpecial);
      return { 'Featured Items': itemsToGroup } as Record<string, MenuItem[]>;
    }
    if (selectedCategory !== 'All') {
      return { [selectedCategory]: filteredItems.filter((item) => item.category === selectedCategory) };
    }

    const grouped: Record<string, MenuItem[]> = {};
    const todaysSpecials = filteredItems.filter((item) => item.isTodaysSpecial);
    const featuredItems = filteredItems.filter((item) => item.isFeatured && !item.isTodaysSpecial);

    if (todaysSpecials.length > 0) {
      grouped["Today's Specials"] = todaysSpecials;
    }
    if (featuredItems.length > 0) {
      grouped['Featured Items'] = featuredItems;
    }

    const regularItems = filteredItems.filter((item) => !item.isTodaysSpecial && !item.isFeatured);
    const regularGrouped = regularItems.reduce((acc: Record<string, MenuItem[]>, item: MenuItem) => {
      if (!acc[item.category]) {
        acc[item.category] = [];
      }
      acc[item.category].push(item);
      return acc;
    }, {});

    return { ...grouped, ...regularGrouped };
  }, [filteredItems, selectedCategory]);

  return { categories, categoryCounts, filteredItems, groupedItems };
}
